import {
  DateFormat,
  diff,
  isPossiblePhoneNumber,
  MenuSelectOption,
  parsePhoneNumber,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';

import { VITE_MAUD_APP_VERSION } from '../constants/env';
import { pageCount as pageCountOptions } from '../constants/list.constants';
import { DateTime } from 'luxon';

const util = {
  capitalize: (s: string): string =>
    s ? `${s[0].toUpperCase()}${s.slice(1)}` : '',
  lowercaseFirstLetter: (s: string): string =>
    s ? `${s[0].toLowerCase()}${s.slice(1)}` : '',

  getEmptyStringError: (text: string, name: string): string =>
    (text ?? '').trim().length ? null : `${name} is required`,
  /** Filtering the tag id's for the tag labels given */
  getTagCodes: (tags: string[], tagDetails: Enumeration[]): string[] => {
    return (tags ?? []).map(
      (eachTag) => tagDetails?.find(({ label }) => eachTag === label).id,
    );
  },
  isAllAddressFieldsEmpty: (addressData: GooglePlacesField): boolean =>
    Object.entries(addressData).every(
      ([key, val]: [string, string | number]) =>
        [
          'addressLine1',
          'addressLine2',
          'city',
          'country',
          'county',
          'name',
          'state',
          'zipCode',
          'placeId',
          'description',
          'timezone',
        ].includes(key)
          ? val === null || val === undefined || val.toString().trim() === ''
          : val === undefined || Number.isNaN(val),
    ),
  isDateOfBirthInFuture: (date: Date): boolean => {
    return (
      diff(
        DateTime.fromJSDate(date).toFormat(DateFormat.longDate),
        DateTime.now().toFormat(DateFormat.longDate),
      ) < 0
    );
  },
  isEmptyArray: (array: Array<any>): boolean => !(array && array.length > 0),
  isEmptyString: (text: string): boolean => !text?.trim().length,
  isPageCountProper: (pageCount: MenuSelectOption): boolean =>
    pageCountOptions.some(
      ({ node, value }: MenuSelectOption) =>
        node === pageCount.node && value === pageCount.value,
    ),
  isValidAddress: (affiliation: GooglePlacesField): boolean =>
    Object.entries(affiliation).every(
      ([key, val]: [string, string | number]) =>
        ['addressLine1', 'city', 'state', 'country', 'zipCode'].includes(key)
          ? val !== null && val.toString().trim() !== ''
          : true,
    ) ||
    Object.entries(affiliation).every(
      ([key, val]: [string, string | number]) =>
        [
          'name',
          'addressLine1',
          'addressLine2',
          'city',
          'state',
          'country',
          'county',
          'zipCode',
          'placeId',
          'timezone',
          'description',
        ].includes(key)
          ? val === null || (val ?? '').toString().trim() === ''
          : val === undefined || Number.isNaN(val),
    ),
  isValidPhonesField: (
    key: string,
    val: string | number,
    isPrimaryRequired: boolean,
    isRequired: boolean,
  ): boolean => {
    const isPrimary = isPrimaryRequired && key === 'mobile';
    if (isPrimary && isRequired) {
      // primary numbers cannot be null
      return (
        val !== null &&
        val?.toString()?.trim() !== '' &&
        util.validatePhoneNumber(val?.toString()?.trim())
      );
    } else if (isRequired) {
      // secondary numbers can be null or empty.
      return (
        val === null ||
        val.toString().trim() === '' ||
        util.validatePhoneNumber(val.toString().trim())
      );
    } else {
      // otherwise, it's not required
      return true;
    }
  },
  isValidPhonesFields: (
    affiliation: PhonesFieldType,
    isPrimaryRequired: boolean,
    requiredFields: Array<string> = [],
  ): boolean =>
    Object.entries(affiliation).every(
      ([key, val]: [string, string | number]) => {
        return util.isValidPhonesField(
          key,
          val,
          isPrimaryRequired,
          requiredFields.includes(key),
        );
      },
    ),
  isValidAffiliation: (
    affiliationNA: boolean,
    affiliation: GooglePlacesField,
  ): boolean => affiliationNA || util.isValidAddress(affiliation),
  isValidStateOnly: (addressData: GooglePlacesField): boolean => {
    const fieldsWithText = Object.entries(addressData).filter(
      ([_, val]: [string, string | number]) => {
        const isEmpty =
          val === undefined ||
          val === null ||
          Number.isNaN(val) ||
          val.toString().trim() === '';

        return !isEmpty;
      },
    );

    if (fieldsWithText.length === 0) {
      return false;
    }

    const isStateOnly = fieldsWithText.every(
      ([key, val]: [string, string | number]) =>
        ['state'].includes(key)
          ? val !== null && val.toString().trim() !== ''
          : false,
    );

    return isStateOnly;
  },
  isValidString: (filter: string): boolean => typeof filter === 'string',
  isValidStringArray: (filters: string[]): boolean => {
    if (Array.isArray(filters)) return filters.every(util.isValidString);
    return false;
  },
  parseErrorMessage: (error: string | undefined, fallback: string): string =>
    typeof error === 'string' && !!error.trim().length ? error : fallback,
  parseJson: (jsonString: string): any => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return {};
    }
  },
  parseNumberCoalescingNaN: (number: number): number => {
    if (isNaN(number)) return 0;
    return Number(number);
  },
  parsePhoneNumberWithDefaultCountry: (phoneNumber = ''): string =>
    phoneNumber ? parsePhoneNumber(phoneNumber, 'US')?.number : null,
  /** Safely removes leading and trailing whitespace */
  trim: (text: string): string => text?.trim(),
  validatePhoneNumber: (phoneNumber: string, allowEmpty = false): boolean =>
    (allowEmpty && phoneNumber === '') || phoneNumber
      ? isPossiblePhoneNumber(phoneNumber, 'US')
      : false,

  getAppVersion: () => {
    return `Version ${VITE_MAUD_APP_VERSION.replaceAll('v', '')}`;
  },
  getTrimmedLowerCaseString: (input: string): string => {
    if (typeof input === 'string' || typeof input === 'number') {
      return input.toString().replaceAll(' ', '').toLowerCase();
    }
    return '';
  },
};

/**
 * Convert an empty string to null, leaving undefined as-is.
 * @param val A string
 */
export const emptyToNull = (val?: string): string | null | undefined =>
  val !== undefined && util.isEmptyString(val) ? null : val;

export const getNameParts = (
  name: string,
): { firstName: string; lastName: string } => {
  if (isEmpty(name)) return null;
  const nameParts = name.split(' ');

  if (nameParts.length === 1) return { firstName: nameParts[0], lastName: '' };

  return { firstName: nameParts[0], lastName: nameParts[nameParts.length - 1] };
};

export default util;
